import React from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "@dr.pogodin/react-helmet";
import "@/index.css";
import { setCatalog } from "@/catalogStore";

// ---------------------------------------------------------------------------
// Strip-on-boot: the default SEO tags shipped in /public/index.html are there
// purely as a fallback for non-JS bots (Bing, social-media link previewers,
// AI crawlers, Googlebot's first crawl pass). At runtime, Helmet manages
// <head> for the SPA. See original note — removing them gives Helmet a clean
// slate so we don't get duplicate title/description/canonical per route.
// ---------------------------------------------------------------------------
document
  .querySelectorAll('head [data-seo-default="1"]')
  .forEach((n) => n.remove());

// ---------------------------------------------------------------------------
// Live catalog hydration. Fetch products/collections from the API and fill the
// catalog store BEFORE importing <App> (which pulls in mock.js and its derived
// exports). This makes catalog edits go live with no rebuild. Bounded by a
// timeout; on any failure we fall back to the bundled JSON already in the store.
// ---------------------------------------------------------------------------
async function loadCatalog() {
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base) return;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 4000);
  try {
    const res = await fetch(`${base}/api/catalog/bootstrap`, {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    if (res.ok) {
      const data = await res.json();
      setCatalog(data.products, data.collections);
    }
  } catch (e) {
    // network error / timeout — keep bundled JSON fallback
  } finally {
    clearTimeout(timer);
  }
}

async function bootstrap() {
  await loadCatalog();
  const { default: App } = await import("@/App");
  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </React.StrictMode>,
  );
}

bootstrap();
